import {
	TBookShipmentShipAPI,
	TShipmentEOS,
	TBookShipmentShipAPIStatus,
	TShipmentStatus,
	TEOSLoadSecurity,
	TLoadRestraints,
	DG_OPTIONS,
	CARGO_TYPE,
	TCargoEOS,
	TBookShipmentStop,
	TShipmentAddress,
	TLoadOptions,
	TBookShipmentDoc,
	TShipmentAddressDoc,
	ImpersonatedShipper
} from '@types';
import {
	getTemperatureId,
	getTruckTypes,
	getTruckSizes,
	isCargoTypeCartonOrSatchel
} from '@utils';
import { getTruckSizeLabel, getTruckTypeLabel } from '@utils/truckSizeTypesLabels';

const buildStatusobj = (statusObj: TBookShipmentShipAPIStatus): TShipmentStatus => {
	const eosStatusObj: TShipmentStatus = {
		code: 0, // statusObj.code,
		name: statusObj.name,
		short: statusObj.short,
		type: '',
		bgcolor: '',
		fgcolor: '',
		canedit: statusObj.canEdit,
		can_admin_edit: statusObj.canAdminEdit,
		candelete: statusObj.canDelete,
		can_admin_delete: statusObj.canAdminDelete,
		icon: statusObj.icon,
		started_at: null,
		last_status_changed_at: null,
		complete_at: null,
		pick_at: null,
		drop_at: null
	}
	return eosStatusObj;
}

const buildLoadSecurity = (secObj: TLoadRestraints): TEOSLoadSecurity => {
	const eosLoadSec: TEOSLoadSecurity = {
		tension_belt: secObj.TENSION_BELTS,
		edge_protect: secObj.EDGE_PROTECTION_SHIP_THIS_FREQUENTLY,
		driver_assist_stop: secObj.DRIVER_ASSISTANCE_AT_STOP,
		anti_slip_mat: secObj.ANTI_SLIP_MAT,
		multilock: secObj.MULTILOCK,
		sideboards: secObj.IS_SIDEBOARDS_FIVE_PER_FIELD,
		chains: secObj.CHAINS,
		stopbars: secObj.STOP_BARS,
		cargo_net: secObj.CARGO_NET,
		coil: secObj.COIL_SUPPORTS,
		tir_cable: secObj.TIR_CABLE,
		asp_pallets: secObj.ANGLES_STRAPS_PLY_FOR_ALL_PALLETS,
		corflute: secObj.CORFLUTE,
		gates: secObj.GATES,
		loading_bars: secObj.LOADING_BARS
	}
	return eosLoadSec;
}

const buildDGMeta = (dgMeta: { [key: string]: boolean }): string[] => {
	const meta: string[] = [];
	Object.entries(dgMeta).forEach((dg) => {
		if (dg[1]) {
			switch (dg[0]) {
			case 'LOAD_CONTAINS_BULK_DANGEROUS_GOODS':
				meta.push(DG_OPTIONS.LOAD_CONTAINS_BULK_DANGEROUS_GOODS);
				break;
			case 'LOAD_CONTAINS_FULLY_REGULATED_DANGEROUS_GOODS':
				meta.push(DG_OPTIONS.LOAD_CONTAINS_FULLY_REGULATED_DANGEROUS_GOODS);
				break;
			case 'LOAD_CONTAINS_LIMITED_QUANTITIES':
				meta.push(DG_OPTIONS.LOAD_CONTAINS_LIMITED_QUANTITIES);
				break;
			default:
				break;
			}
		}
	})
	return meta;
}

const buildPalletHandling = (
	pickupModifiers: { [key: string]: boolean } = {},
	dropoffModifiers: { [key: string]: boolean } = {}
): string[] => {
	const palletHandling: string [] = [];
	const modifierKeys = new Set([...Object.keys(pickupModifiers), ...Object.keys(dropoffModifiers)]);
	modifierKeys.forEach((key) => {
		if (pickupModifiers[key] || dropoffModifiers[key]) {
			switch (key) {
			case 'PALLET_JACK':
				palletHandling.push('pallet_jack_required');
				break;
			case 'PALLET_TRANSFER':
				palletHandling.push('pallet_transfer_required');
				break;
			case 'PALLET_EXCHANGE':
				palletHandling.push('pallet_exchange_required');
				break;
			default:
				break;
			}
		}
	});
	return palletHandling;
}

const buildShipmentDocs = (docs: TBookShipmentDoc[]): Partial<TShipmentAddressDoc>[] => {
	const newDocs: Partial<TShipmentAddressDoc>[] = [];
	docs.forEach((doc) => {
		const newDoc: Partial<TShipmentAddressDoc> = {
			shipmentAPIid: doc.id,
			document: {
				name: doc.name,
				path: doc.url
			}
		}
		newDocs.push(newDoc);
	});
	return newDocs;
}

/* The stop's ordinal in the API's ordered stops. EOS stores the same ordinal on the cargo as
   pick_position / drop_position at ingest, so carrying it here keeps the two producers in step. */
const findStopIndex = (shipmentObj: TBookShipmentShipAPI, action: 'PICKUP' | 'DROPOFF', cargoIndex: number): number | null => {
	const index = shipmentObj.stops.findIndex((stop, stopIndex) => {
		const isPick = stop.action_type === action;
		const isStopForCargo = shipmentObj.cargo_stops.some((cs) => cs.stop_index === stopIndex && cs.cargo_index === cargoIndex);
		return isPick && isStopForCargo;
	});
	return index === -1 ? null : index;
}

const buildShipmentAddress = (stop: TBookShipmentStop | null, loadOpts: TLoadOptions): TShipmentAddress => {
	const newAddress: TShipmentAddress = {
		id: 0,
		address: stop?.location.text ?? '',
		block_addr: stop?.location.text ?? '',
		business_name: stop?.business_name ?? '',
		contact: stop?.contact ?? '',
		phone: stop?.phone ?? '',
		email: stop?.email ?? '',
		instructions: stop?.instructions ?? '',
		slot: stop?.time_slot_details ?? '',
		gate_house: '',
		has_time_slot: stop?.time_slot_status !== 'NOT_REQUIRED',
		is_mandatory: stop?.time_slot_status !== 'NOT_REQUIRED',
		open_time: stop?.opening_time ?? '',
		close_time: stop?.closing_time ?? '',
		carrier_must_book_slot: stop?.time_slot_status === 'OFLOAD_MUST_BOOK',
		carrier_must_call_before: stop?.time_slot_status === 'BOOKED',
		side_load: loadOpts.FROM_SIDE ?? false,
		rear_load: loadOpts.FROM_BACK ?? false,
		top_load: loadOpts.FROM_TOP ?? false,
		lift_gate: loadOpts.FROM_LIFTGATE ?? false,
		requires_tailgate_lift: stop?.modifiers?.TAILGATE ?? false,
		requires_hand_unload: stop?.modifiers?.HAND_UNLOAD ?? false,
		truck_sizes_requiring_split: stop?.truck_sizes_requiring_split ?? [],
		loading_requirements: '',
		needs_ppe: false,
		// --> Need to come back to these...
		street: '',
		state: '',
		suburb: '',
		subpost: '',
		postcode: '',
		locality_type: stop?.location.locality_type,
		stopId: stop?.id ?? '',
		managed_location_external_id: stop?.managed_location_external_id,
		shipment_address_documents: buildShipmentDocs(stop?.documents ?? [])
	}

	return newAddress;
}

/* Bundle is a sibling of Other: freeform freight measured by total quantity, not pallet spaces,
   so it reads other_quantity the same way Other does rather than the pallet fields. */
const OTHER_FAMILY_CARGO_TYPES = [CARGO_TYPE.OTHER, CARGO_TYPE.BUNDLE, CARGO_TYPE.SKID];

/* A shipment mixing row types (e.g. Pallets + Bundle from the row editor) reads as OTHER, the flow
   whose payload builders keep each row's own cargo_type; a single-type shipment keeps that type. */
const getShipmentCargoType = (shipmentObj: TBookShipmentShipAPI): CARGO_TYPE => {
	const rowTypes = new Set(shipmentObj.cargos.map((cargo) => cargo.cargo_type));
	if (rowTypes.size > 1) return CARGO_TYPE.OTHER;
	return shipmentObj.cargos[0].cargo_type as CARGO_TYPE;
}

const buildCargos = (shipmentObj: TBookShipmentShipAPI): TCargoEOS[] => {
	const cargo: TCargoEOS[] = [];
	for (let i=0, l=shipmentObj.cargos.length; i<l; ++i) {
		const currentCargo = shipmentObj.cargos[i];
		const isOtherFamilyRow = OTHER_FAMILY_CARGO_TYPES.includes(currentCargo.cargo_type);
		const pickupIndex = findStopIndex(shipmentObj, 'PICKUP', i);
		const dropoffIndex = findStopIndex(shipmentObj, 'DROPOFF', i);
		const pickupStop = pickupIndex === null ? null : shipmentObj.stops[pickupIndex];
		const dropoffStop = dropoffIndex === null ? null : shipmentObj.stops[dropoffIndex];

		const newCargo: TCargoEOS = {
			id: 0,
			cargo_type: currentCargo.cargo_type,
			cargo_type_id: 0,
			cargo_trans_type_id: 0,
			pick_from: pickupStop ? pickupStop.arrive_from ?? '' : '',
			pick_to: pickupStop ? pickupStop.depart_by ?? '' : '',
			drop_from: dropoffStop ? dropoffStop.arrive_from ?? '' : '',
			drop_to: dropoffStop ? dropoffStop.depart_by ?? '' : '',
			pickup_at: pickupStop ? new Date(pickupStop.action_on) : '',
			drop_at: dropoffStop ? new Date(dropoffStop.action_on) : '',
			is_ftl: currentCargo.cargo_type === CARGO_TYPE.FTL,
			qty: isOtherFamilyRow ? currentCargo.other_quantity ?? 0 : currentCargo.pallet_space_quantity ?? 0,
			is_pallet_spaces: currentCargo.pallet_space_quantity !== null,
			load_mtr: currentCargo.load_meter,
			weight: currentCargo.total_weight_kg ?? undefined,
			total_weight: currentCargo.total_weight_kg ?? undefined,
			height: currentCargo.height_cm,
			width: currentCargo.width_cm,
			length: currentCargo.length_cm,
			pallet_qty: isOtherFamilyRow ? undefined : (currentCargo.pallet_quantity ?? 0),
			pick_position: pickupIndex ?? undefined,
			drop_position: dropoffIndex ?? undefined,
			pickup: buildShipmentAddress(pickupStop, shipmentObj.loading_options),
			dropoff: buildShipmentAddress(dropoffStop, shipmentObj.loading_options),
			is_begin: false,
			is_done: false,
			has_overweight_pallet: currentCargo.overweight_details !== null && currentCargo.overweight_details !== '',
			overweight_pallets_information: currentCargo.overweight_details,
			freight_detail: currentCargo.freight_details,
			total_cubic_volume_mts: currentCargo.total_cubic_volume_mts ?? undefined,
			...(currentCargo.cargo_item?.item_id ? {
				cargo_item: {
					item_id: currentCargo.cargo_item?.item_id,
					name: currentCargo.cargo_item?.name,
					weight_kg: currentCargo.cargo_item?.weight_kg,
					description: currentCargo.cargo_item?.description,
					charge_type: currentCargo.cargo_item?.charge_type
				}
			} : {} ),
			...(isCargoTypeCartonOrSatchel(currentCargo.cargo_type as CARGO_TYPE) ? { machship_items: currentCargo.machship_items } : {})
		}

		cargo.push(newCargo);
	}
	return cargo;
}

const getTruckTypeID = (truck: string): number => {
	const theTruck = getTruckTypes.find((t) => t.label === getTruckTypeLabel(truck));
	if (theTruck) return theTruck.value;
	return 0;
}

const getTruckSizeID = (truck: string | null): number => {
	if (!truck) return 0;
	const theTruck = getTruckSizes.find((t) => t.label === getTruckSizeLabel(truck));
	if (theTruck) return theTruck.value;
	return 0;
}

export const buildEOSShipmentSchema = (shipmentObj: TBookShipmentShipAPI, shipper: ImpersonatedShipper | null): TShipmentEOS => {
	const EOSShipment: TShipmentEOS = {
		shipper_id: shipper?.id,
		bus_name: shipper?.name ?? '',
		pick_addr: '',
		drop_addr: '',
		master_ship_id: null,
		master_shipment_ref: shipmentObj.master_shipment?.reference ?? '',
		manifest_id: null,
		reference: shipmentObj.reference ?? '',
		shipper_ref1: shipmentObj.shipper_reference ?? '',
		po_reference: shipmentObj.purchase_order_number ?? null,
		shipper_ref2: null,
		shipper_ref3: null,
		cargo_type: getShipmentCargoType(shipmentObj),
		cargo_type_id: 0,
		quote_id: null,
		temperature_id: getTemperatureId(shipmentObj.cargos[0].temperature ?? 'AMBIENT'),
		total_weight: '',
		total_volume: '',
		req_truck_size_id: getTruckSizeID(shipmentObj.truck_size),
		req_truck_type_id: getTruckTypeID(shipmentObj.truck_type),
		transport_type_id: 0,
		is_stackable: false,
		need_forklift: false,
		need_pallet_jack: shipmentObj.stops[0].modifiers?.PALLET_JACK ?? false,
		ship_status: 0,
		ship_status_id: 0,
		has_claim: false,
		want_side_load: shipmentObj.loading_options.FROM_SIDE,
		want_rear_load: shipmentObj.loading_options.FROM_BACK,
		want_top_load: shipmentObj.loading_options.FROM_TOP,
		want_tail_lift: shipmentObj.loading_options.FROM_LIFTGATE,
		cargo_id: 0,
		item_number: null,
		qty: 0,
		pallet_qty: 0,
		load_mtr: null,
		weight: null,
		height: '',
		length: '',
		width: '',
		is_ftl: shipmentObj.cargos[0].cargo_type as CARGO_TYPE === CARGO_TYPE.FTL,
		pick_position: null,
		drop_position: null,
		pickup_at: new Date(shipmentObj.stops[0].action_on),
		drop_at: new Date(shipmentObj.stops[shipmentObj.stops.length - 1].action_on),
		pick_from: shipmentObj.stops[0].arrive_from ?? '',
		pick_to: shipmentObj.stops[0].depart_by ?? '',
		drop_from: shipmentObj.stops[shipmentObj.stops.length - 1].arrive_from ?? '',
		drop_to: shipmentObj.stops[shipmentObj.stops.length - 1].depart_by ?? '',
		created_at: new Date(),
		updated_at: new Date(),
		create_user: '',
		edit_user: null,
		truck_type_name: shipmentObj.truck_type,
		cargos: buildCargos(shipmentObj),
		is_dangerous: shipmentObj.cargos[0].contains_dangerous_goods,
		dangerous_good_meta_data: buildDGMeta(shipmentObj.cargos[0].dangerous_goods_data),
		status: shipmentObj.status ? buildStatusobj(shipmentObj.status) : null,
		load_security: buildLoadSecurity(shipmentObj.load_restraints),
		pallet_handling: buildPalletHandling(shipmentObj.stops[0].modifiers, shipmentObj.stops[shipmentObj.stops.length - 1].modifiers),
		partialExpires: shipmentObj.expires_at_epoch_seconds,
		cost: shipmentObj.price_data ?? undefined,
		freight_mode: shipmentObj.freight_mode,
		accepts_tnc: shipmentObj.accepts_terms_and_conditions ?? undefined,
		total_qty: 1,
		total_pallet_qty: 1,
		pricing_method: shipmentObj.price_data?.rateCardType ?? ''
	}

	return EOSShipment;
}
